import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { I18nProvider } from "./i18n";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import DashboardLayout from "./layouts/DashboardLayout";

import Login from "./pages/Login";
import RegisterClinic from "./pages/RegisterClinic";
import ChangePassword from "./pages/ChangePassword";
import Dashboard from "./pages/Dashboard";
import Patients from "./pages/Patients";
import PatientDetail from "./pages/PatientDetail";
import Appointments from "./pages/Appointments";
import Consultations from "./pages/Consultations";
import Invoices from "./pages/Invoices";
import Payments from "./pages/Payments";
import Cash from "./pages/Cash";
import Stock from "./pages/Stock";
import Users from "./pages/Users";
import Reports from "./pages/Reports";
import Insights from "./pages/Insights";
import PlatformTenants from "./pages/PlatformTenants";
import PlatformPlans from "./pages/PlatformPlans";
import PlatformLayout from "./layouts/PlatformLayout";
import Documentation from "./pages/Documentation";
import Billing from "./pages/Billing";
import BillingCallback from "./pages/BillingCallback";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-ink-muted">Chargement…</div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  if (user?.must_change_password && loc.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }
  // Super admin is a platform user and CANNOT access tenant-scoped routes (/app/*)
  if (user.role === "super_admin" && loc.pathname.startsWith("/app")) {
    return <Navigate to="/platform/tenants" replace />;
  }
  if (roles && !roles.includes(user.role)) return <Navigate to="/app" replace />;
  return children;
}

function ProtectedPlatform({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return null;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  if (user.role !== "super_admin") return <Navigate to="/app" replace />;
  return children;
}

function Home() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user?.role === "super_admin") return <Navigate to="/platform/tenants" replace />;
  return <Navigate to={user ? "/app" : "/login"} replace />;
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<RegisterClinic />} />
            <Route path="/change-password" element={<Protected><ChangePassword /></Protected>} />

            <Route path="/app" element={<Protected><DashboardLayout><Dashboard /></DashboardLayout></Protected>} />
            <Route path="/app/patients" element={<Protected><DashboardLayout><Patients /></DashboardLayout></Protected>} />
            <Route path="/app/patients/:id" element={<Protected><DashboardLayout><PatientDetail /></DashboardLayout></Protected>} />
            <Route path="/app/appointments" element={<Protected><DashboardLayout><Appointments /></DashboardLayout></Protected>} />
            <Route path="/app/consultations" element={<Protected roles={["owner", "doctor", "nurse"]}><DashboardLayout><Consultations /></DashboardLayout></Protected>} />
            <Route path="/app/invoices" element={<Protected roles={["owner", "secretary", "cashier", "doctor"]}><DashboardLayout><Invoices /></DashboardLayout></Protected>} />
            <Route path="/app/payments" element={<Protected roles={["owner", "secretary", "cashier"]}><DashboardLayout><Payments /></DashboardLayout></Protected>} />
            <Route path="/app/cash" element={<Protected roles={["owner", "cashier"]}><DashboardLayout><Cash /></DashboardLayout></Protected>} />
            <Route path="/app/stock" element={<Protected roles={["owner", "stock_manager", "doctor", "nurse"]}><DashboardLayout><Stock /></DashboardLayout></Protected>} />
            <Route path="/app/users" element={<Protected roles={["owner"]}><DashboardLayout><Users /></DashboardLayout></Protected>} />
            <Route path="/app/reports" element={<Protected><DashboardLayout><Reports /></DashboardLayout></Protected>} />
            <Route path="/app/documentation" element={<Protected><DashboardLayout><Documentation /></DashboardLayout></Protected>} />
            <Route path="/app/insights" element={<Protected roles={["owner", "admin"]}><DashboardLayout><Insights /></DashboardLayout></Protected>} />
            <Route path="/app/billing" element={<Protected roles={["owner"]}><DashboardLayout><Billing /></DashboardLayout></Protected>} />
            <Route path="/app/billing/callback" element={<Protected><BillingCallback /></Protected>} />

            {/* Platform (Super Admin only) */}
            <Route path="/platform" element={<ProtectedPlatform><Navigate to="/platform/tenants" replace /></ProtectedPlatform>} />
            <Route path="/platform/tenants" element={<ProtectedPlatform><PlatformLayout><PlatformTenants /></PlatformLayout></ProtectedPlatform>} />
            <Route path="/platform/plans" element={<ProtectedPlatform><PlatformLayout><PlatformPlans /></PlatformLayout></ProtectedPlatform>} />
            <Route path="/platform/documentation" element={<ProtectedPlatform><PlatformLayout><Documentation /></PlatformLayout></ProtectedPlatform>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}
